/**
 * Athos Beacon 2.0 analytics tracker
 *
 * Beacon 2.0 is a reference-based standard: each event sends a small `{ context, data }` envelope
 * to a per-event endpoint, referencing results by `responseId` + `uid`/`parentId`
 * rather than embedding full product mappings.
 *
 * Beacon 2.0 events live in four surface families:
 *   {origin}/{siteId}/{search|autocomplete|category|recommendations}/{render|impression|clickthrough}
 *
 * Docs: https://docs.athoscommerce.com/reference/recommendations-tracking-guide
 */

import { session } from '@/components/helpers/session'
import { globalState } from '@/components/helpers/utils'
import { getBeaconOrigin, getAthosSiteID } from 'libs/athos/athos-utils'
import type { AthosResult } from 'libs/athos/athos.types'

export const BEACON_EVENTS = {
  RENDER: 'render',
  IMPRESSION: 'impression',
  CLICKTHROUGH: 'clickthrough'
}

export type BeaconSurface = 'search' | 'autocomplete' | 'category' | 'recommendations'

// Identifies this integration as the source of beacon events.
const BEACON_INITIATOR = 'athos/reebok-theme/2.0'

// text/plain avoids a CORS preflight on the beacon POST.
const BEACON_CONTENT_TYPE = 'text/plain'

const BEACON_LOG_KEY = 'athosBeaconLog'
const BEACON_LOG_LIMIT = 50

function recordBeaconEvent(eventType: string, url: string, payload: Record<string, unknown>): void {
  if (typeof sessionStorage === 'undefined') return
  try {
    const raw = sessionStorage.getItem(BEACON_LOG_KEY)
    const log = raw ? JSON.parse(raw) : []
    log.push({ timestamp: new Date().toISOString(), eventType, url, payload })
    if (log.length > BEACON_LOG_LIMIT) log.splice(0, log.length - BEACON_LOG_LIMIT)
    sessionStorage.setItem(BEACON_LOG_KEY, JSON.stringify(log))
  } catch {
    // Debug logging must never break tracking.
  }
}

function getBeaconLog(): Array<Record<string, unknown>> {
  if (typeof sessionStorage === 'undefined') return []
  try {
    const raw = sessionStorage.getItem(BEACON_LOG_KEY)
    return raw ? JSON.parse(raw) : []
  } catch {
    return []
  }
}

if (typeof window !== 'undefined') {
  (window as any).__athosBeacon = {
    table: () => console.table(getBeaconLog()),
    json: () => getBeaconLog(),
    clear: () => sessionStorage.removeItem(BEACON_LOG_KEY)
  }
}

export class AthosBeacon {
  type: BeaconSurface
  profile?: string
  responseId?: string
  siteId: string

  /**
   * @param type — the Beacon 2.0 surface family (`search` | `autocomplete` | `category` | `recommendations`)
   * @param profile — the recommendation profile tag (Beacon `data.tag`). Only recommendations carry one.
   * @param responseId — the Athos API `responseId` for this surface
   */
  constructor(type: BeaconSurface = 'recommendations', profile?: string, responseId?: string) {
    this.type = type
    this.profile = profile
    this.responseId = responseId
    this.siteId = getAthosSiteID()
  }

  setResponseId(responseId: string): void {
    this.responseId = responseId
  }

  endpoint(eventType: string): string {
    return `${getBeaconOrigin()}/${this.siteId}/${this.type}/${eventType}`
  }

  /**
   * Shared Beacon 2.0 context envelope.
   * `website.trackingCode` (Beacon 1.0) is removed in 2.0.
   */
  generateContext() {
    const shopperId = session.shopper.get()
    const currencyCode = globalState.localization?.currency

    return {
      timestamp: new Date().toISOString(), // RFC3339 / UTC
      pageUrl: window.location.href,
      userAgent: navigator.userAgent,
      userId: session.userId.get(),
      sessionId: session.sessionId.get(),
      pageLoadId: session.pageLoadId.get(),
      initiator: BEACON_INITIATOR,
      ...(shopperId && { shopperId }),
      ...(currencyCode && { currency: { code: currencyCode } })
    }
  }

  /**
   * Reference an individual result by type + ids only (reference-based tracking).
   * `uid` is the variant id, `parentId` the product id. When the feed only exposes
   * a product-level id we fall back to it for both — Beacon requires both to be
   * present and non-empty.
   */
  buildResultReference(result: AthosResult) {
    const uid = result.uid ?? result.id
    const parentId = result.parentId ?? result.groupId ?? uid
    return {
      type: 'product',
      uid: `${uid}`,
      parentId: `${parentId}`
    }
  }

  /**
   * POST a beacon event. Clickthrough uses `sendBeacon`/`keepalive` so it survives
   * the navigation triggered by the click.
   */
  send(eventType: string, data: Record<string, unknown>, { keepalive = false } = {}): void | Promise<unknown> {
    if (!this.siteId || !this.responseId) return

    const payload = {
      context: this.generateContext(),
      data: {
        ...(this.profile && { tag: this.profile }),
        responseId: this.responseId,
        ...data
      }
    }

    const url = this.endpoint(eventType)
    const body = JSON.stringify(payload)

    recordBeaconEvent(eventType, url, payload)

    if (keepalive && typeof navigator !== 'undefined' && navigator.sendBeacon) {
      navigator.sendBeacon(url, body)
      return
    }

    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': BEACON_CONTENT_TYPE },
      body,
      keepalive
    }).catch(() => {
      // Tracking must never break the storefront.
    })
  }

  /**
   * Render — fired once when a recommendation surface is rendered.
   * Payload: { data: { tag, responseId } }
   */
  render() {
    return this.send(BEACON_EVENTS.RENDER, {})
  }

  /**
   * Impression — the visible recommendation results.
   * Personalization surfaces are permitted to batch the visible array in a single
   * impression event (rather than per-card).
   * Payload: { data: { tag, responseId, results: [{type,uid,parentId}], banners: [] } }
   */
  impression(results: AthosResult[]) {
    if (!Array.isArray(results) || results.length === 0) return
    return this.send(BEACON_EVENTS.IMPRESSION, {
      results: results.map((result) => this.buildResultReference(result)),
      banners: []
    })
  }

  /**
   * Clickthrough — a single clicked result.
   * Payload: { data: { tag, responseId, results: [{type,uid,parentId}] } }
   */
  clickthrough(result: AthosResult) {
    if (!result) return
    return this.send(
      BEACON_EVENTS.CLICKTHROUGH,
      { results: [this.buildResultReference(result)] },
      { keepalive: true }
    )
  }
}
